import loadable from '@loadable/component';
import { IconMap, IconName } from 'shared/icons/types';

/**
 * Light theme icon map.
 * Each entry uses @loadable/component so webpack splits every icon into its own chunk.
 * Only icons that are actually rendered will be fetched — unused icons cost zero bytes.
 * @loadable/component supports SSR (unlike React.lazy + Suspense on React 16).
 *
 * Total: 125 unique icons across all categories
 */

export const lightThemeIconMap: IconMap = {
  // Navigation & Branding
  [IconName.HOME]: {
    default: loadable(() => import('./adapters/NykaaLogoAdapter')),
    defaultColor: 'textPrimary',
  },
  [IconName.LOGO]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/nykaa-n-logo')),
    defaultColor: 'textPrimary',
  },
  [IconName.HAMBURGER_MENU]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/hamburger-menu')),
    defaultColor: 'textPrimary',
  },
  [IconName.CATEGORIES]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/shop')),
    filled: loadable(() => import('@nykaa/ui-components/Icons/shop-filled')),
    defaultColor: 'textPrimary',
    filledColor: 'secondary',
  },
  [IconName.SALE]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/offers')),
    filled: loadable(() => import('@nykaa/ui-components/Icons/offer-filled')),
    defaultColor: 'textPrimary',
    filledColor: 'secondary',
  },
  [IconName.ACCOUNT]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/account-more')),
    filled: loadable(() => import('@nykaa/ui-components/Icons/user-filled')),
    defaultColor: 'textPrimary',
    filledColor: 'secondary',
  },
  [IconName.COUPONS_OFFERS]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/coupon')),
    filled: loadable(() => import('shared/svg/CouponOffersFilled')),
    defaultColor: 'textPrimary',
    filledColor: 'secondary',
  },
  [IconName.HNM]: {
    default: loadable(() => import('shared/svg/HnMIcon/HnMIcon')),
    filled: loadable(() => import('shared/svg/HnMIcon/HnMFilled')),
    defaultColor: 'textPrimary',
    filledColor: 'hnmTabActiveColor',
  },
  [IconName.GLOBAL_LUXE]: {
    default: loadable(() => import('shared/svg/GlobalLuxe/GlobalLuxeIcon')),
    filled: loadable(() => import('shared/svg/GlobalLuxe/GlobalLuxeFilled')),
    defaultColor: 'textSecondary',
    filledColor: 'textSecondary',
  },
  [IconName.IND_LUXE]: {
    default: loadable(() => import('shared/svg/IndLuxeIcon/IndLuxeIcon')),
    filled: loadable(() => import('shared/svg/IndLuxeIcon/IndLuxeFilled')),
    defaultColor: 'textSecondary',
    filledColor: 'textSecondary',
  },
  [IconName.ATELIER_LOGO]: {
    default: loadable(() => import('shared/svg/atelier/AtelierLogo')),
    defaultColor: 'textPrimary',
  },

  // Arrows & Chevrons
  [IconName.ARROW_LEFT]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/arrow-left')),
    defaultColor: 'textPrimary',
  },
  [IconName.ARROW_RIGHT]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/arrow-right')),
    defaultColor: 'textPrimary',
  },
  [IconName.BACK]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/arrow-left')),
    defaultColor: 'textPrimary',
  },
  [IconName.CHEVRON_LEFT]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/chevron-left')),
    defaultColor: 'textPrimary',
  },
  [IconName.CHEVRON_RIGHT]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/chevron-right')),
    defaultColor: 'textPrimary',
  },
  [IconName.CHEVRON_UP]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/chevron-up')),
    defaultColor: 'textPrimary',
  },
  [IconName.CHEVRON_DOWN]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/chevron-down')),
    defaultColor: 'textPrimary',
  },
  [IconName.CHEVRON_UP_SMALL]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/chevron-up-small')),
    defaultColor: 'textPrimary',
  },
  [IconName.CHEVRON_DOWN_SMALL]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/chevron-down-small')),
    defaultColor: 'textPrimary',
  },
  [IconName.CHEVRON_LEFT_SMALL]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/chevron-left-small')),
    defaultColor: 'textPrimary',
  },
  [IconName.CHEVRON_RIGHT_SMALL]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/chevron-right-small')),
    defaultColor: 'textPrimary',
  },

  // Close & Remove
  [IconName.CLOSE]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/close')),
    defaultColor: 'primary40',
  },
  [IconName.CIRCLE_X_DUOTONE]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/circle-x-duotone')),
    defaultColor: 'primary40',
  },
  [IconName.CIRCLE_X_FILLED]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/circle-x-filled')),
    defaultColor: 'primary40',
  },
  [IconName.TRASH_CAN]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/trash-can')),
    defaultColor: 'primary40',
  },

  // Actions
  [IconName.SEARCH]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/search')),
    defaultColor: 'textPrimary',
  },
  [IconName.SEARCH_RECENT]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/search-recent')),
    defaultColor: 'primary40',
  },
  [IconName.FILTER]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/filter')),
    defaultColor: 'textPrimary',
  },
  [IconName.SORT]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/sort')),
    defaultColor: 'textPrimary',
  },
  [IconName.EDIT]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/edit')),
    defaultColor: 'primary40',
  },
  [IconName.EDIT_1]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/edit-1')),
    defaultColor: 'primary40',
  },
  [IconName.COPY]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/copy')),
    defaultColor: 'primary40',
  },
  [IconName.SHARE_ANDROID]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/share-android')),
    defaultColor: 'textPrimary',
  },
  [IconName.LINK]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/link')),
    defaultColor: 'primary40',
  },
  [IconName.RESET]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/reset')),
    defaultColor: 'primary40',
  },
  [IconName.PLUS]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/plus')),
    defaultColor: 'textPrimary',
  },
  [IconName.MINUS]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/minus')),
    defaultColor: 'textPrimary',
  },

  // Alerts & Status
  [IconName.ALERT_POSITIVE]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/alert-positive')),
    defaultColor: 'positivePrimary',
  },
  [IconName.ALERT_POSITIVE_FILLED]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/alert-positive-filled')),
    defaultColor: 'positivePrimary',
  },
  [IconName.ALERT_NEGATIVE]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/alert-negative')),
    defaultColor: 'textPrimary',
  },
  [IconName.ALERT_NEGATIVE_FILLED]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/alert-negative-filled')),
    defaultColor: 'textPrimary',
  },
  [IconName.ALERT_WARNING]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/alert-warning')),
    defaultColor: 'warning',
  },
  [IconName.ALERT_WARNING_FILLED]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/alert-warning-filled')),
    defaultColor: 'currentColor',
  },
  [IconName.ALERT_INFO]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/alert-info')),
    defaultColor: 'currentColor',
  },
  [IconName.ALERT_INFO_FILLED]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/alert-info-filled')),
    defaultColor: 'currentColor',
  },
  [IconName.NOTIFICATION_BELL]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/notification-bell')),
    defaultColor: 'textPrimary',
  },
  [IconName.CIRCLE_TICK]: {
    default: loadable(() => import('shared/svg/CircleTick')),
    defaultColor: 'currentColor',
  },

  // Ratings & Reviews
  [IconName.STAR]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/star')),
    defaultColor: 'currentColor',
  },
  [IconName.STAR_FILLED]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/star-filled')),
    defaultColor: 'currentColor',
  },
  [IconName.STAR_HALF_FILLED]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/star-half-filled')),
    defaultColor: 'currentColor',
  },
  [IconName.THUMBS_UP]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/thumbs-up')),
    filled: loadable(() => import('@nykaa/ui-components/Icons/thumbs-up-filled')),
    defaultColor: 'primary40',
    filledColor: 'secondary',
  },

  // Shopping
  [IconName.CART]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/shopping-bag')),
    defaultColor: 'textPrimary',
  },
  [IconName.WISHLIST]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/heart')),
    filled: loadable(() => import('@nykaa/ui-components/Icons/heart-filled')),
    defaultColor: 'textPrimary',
    filledColor: 'secondary',
  },
  [IconName.HEART_PLUS]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/heart-plus')),
    defaultColor: 'secondary',
  },
  [IconName.SIMILAR_ITEMS]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/similar-items')),
    defaultColor: 'textPrimary',
  },
  [IconName.CIRCLE_EXCHANGE]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/circle-exchange')),
    defaultColor: 'textPrimary',
  },
  [IconName.SELLER_UPDATED]: {
    default: loadable(() => import('shared/svg/SellerUpdated')),
    defaultColor: 'textPrimary',
  },

  // Delivery & Orders
  [IconName.DELIVERY_1]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/delivery-1')),
    defaultColor: 'primary40',
  },
  [IconName.DELIVERY_2]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/delivery-2')),
    defaultColor: 'primary40',
  },
  [IconName.BOX]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/box')),
    defaultColor: 'primary40',
  },
  [IconName.SPECIAL_OFFERS]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/special-offers')),
    defaultColor: 'secondary',
  },
  [IconName.OFFER_FILLED]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/offer-filled')),
    defaultColor: 'secondary',
  },

  // Gifts
  [IconName.GIFT_CARD]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/gift-card')),
    defaultColor: 'primary40',
  },
  [IconName.GIFT_BOX_2]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/gift-box-2')),
    defaultColor: 'primary40',
  },

  // User
  [IconName.USER]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/user')),
    defaultColor: 'textPrimary',
  },
  [IconName.USER_FILLED]: {
    default: loadable(() => import('./adapters/UserFilledAdapter')),
    defaultColor: 'primary',
  },
  [IconName.NYKAA_N_HEART]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/nykaa-n-heart')),
    defaultColor: 'secondary',
  },

  // Location
  [IconName.LOCATION_MARKER_FILLED]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/location-marker-filled')),
    defaultColor: 'currentColor',
  },
  [IconName.LOCATION_PIN]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/location-pin')),
    defaultColor: 'currentColor',
  },

  // Media
  [IconName.CAMERA]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/camera')),
    defaultColor: 'primary40',
  },
  [IconName.IMAGE]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/image')),
    defaultColor: 'primary40',
  },
  [IconName.EYE]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/eye')),
    defaultColor: 'primary40',
  },
  [IconName.CIRCLE_PLAY_FILLED]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/circle-play-filled')),
    defaultColor: 'currentColor',
  },
  [IconName.TREND_LINE]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/trend-line')),
    defaultColor: 'primary40',
  },
  [IconName.FLIP_CAMERA]: {
    default: loadable(() => import('shared/svg/FlipCamera')),
    defaultColor: 'currentColor',
  },
  [IconName.DRAG_N_DROP]: {
    default: loadable(() => import('shared/svg/DragNDrop')),
    defaultColor: 'currentColor',
  },

  // System UI
  [IconName.CLOCK]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/clock')),
    defaultColor: 'primary40',
  },
  [IconName.MOBILE]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/mobile')),
    defaultColor: 'primary40',
  },
  [IconName.HELP]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/help')),
    defaultColor: 'primary40',
  },
  [IconName.CHAT]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/chat')),
    defaultColor: 'primary40',
  },
  [IconName.CHAT_BUBBLE]: {
    default: loadable(() => import('shared/svg/ChatBubble')),
    defaultColor: 'currentColor',
  },
  [IconName.DOCUMENT]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/document')),
    defaultColor: 'primary40',
  },
  [IconName.PRIVACY_POLICY]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/privacy-policy')),
    defaultColor: 'primary40',
  },
  [IconName.ADDRESS_BOOK]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/address-book')),
    defaultColor: 'primary40',
  },
  [IconName.SAVED_PAYMENTS]: {
    default: loadable(() => import('shared/svg/SavedPayments')),
    defaultColor: 'primary40',
  },
  [IconName.GLOBE]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/globe')),
    defaultColor: 'primary40',
  },
  [IconName.LEAVES]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/leaves')),
    defaultColor: 'primary40',
  },
  [IconName.CARD_DEBIT_CREDIT]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/card-debit-credit')),
    defaultColor: 'primary40',
  },
  [IconName.CIRCLE_LOCK]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/circle-lock')),
    defaultColor: 'primary40',
  },

  // Other
  [IconName.DROPDOWN_FILLED]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/dropdown-filled')),
    defaultColor: 'currentColor',
  },
  [IconName.CIRCLE_ARROW_UP_FILLED]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/circle-arrow-up-filled')),
    defaultColor: 'currentColor',
  },
  [IconName.CIRCLE_PLUS_FILLED]: {
    default: loadable(() => import('@nykaa/ui-components/Icons/circle-plus-filled')),
    defaultColor: 'currentColor',
  },

  // Additional Icons from shared/svg
  [IconName.ABOUT_US]: {
    default: loadable(() => import('shared/svg/AboutUs/AboutUs')),
    defaultColor: 'textPrimary',
  },
  [IconName.AI_STAR]: {
    default: loadable(() => import('shared/svg/AiStar')),
    defaultColor: 'currentColor',
  },
  [IconName.AUTHENTICATION_ICON]: {
    default: loadable(() => import('shared/svg/AuthenticationIcon')),
    defaultColor: 'currentColor',
  },
  [IconName.BELL]: {
    default: loadable(() => import('shared/svg/Bell')),
    defaultColor: 'textPrimary',
  },
  [IconName.BIN]: {
    default: loadable(() => import('shared/svg/Bin')),
    defaultColor: 'primary40',
  },
  [IconName.BORDER_HALF_CIRCLES]: {
    default: loadable(() => import('shared/svg/BorderHalfCircles')),
    defaultColor: 'currentColor',
  },
  [IconName.CAMERA_SVG]: {
    default: loadable(() => import('shared/svg/Camera')),
    defaultColor: 'primary40',
  },
  [IconName.CARD]: {
    default: loadable(() => import('shared/svg/Card')),
    defaultColor: 'currentColor',
  },
  [IconName.CHECK_MINUS]: {
    default: loadable(() => import('shared/svg/CheckMinus')),
    defaultColor: 'currentColor',
  },
  [IconName.CIRCLE_ARROW]: {
    default: loadable(() => import('shared/svg/CircleArrow')),
    defaultColor: 'currentColor',
  },
  [IconName.CIRCLE_EXCLAMATION]: {
    default: loadable(() => import('shared/svg/CircleExclamation')),
    defaultColor: 'warning',
  },
  [IconName.CIRCLE_INFO]: {
    default: loadable(() => import('shared/svg/CircleInfo')),
    defaultColor: 'currentColor',
  },
  [IconName.COPY_SVG]: {
    default: loadable(() => import('shared/svg/Copy')),
    defaultColor: 'primary40',
  },
  [IconName.COUPON_ICON_SUCCESS]: {
    default: loadable(() => import('shared/svg/CouponIconSuccess')),
    defaultColor: 'positive',
  },
  [IconName.COUPON_LOCK]: {
    default: loadable(() => import('shared/svg/CouponLock')),
    defaultColor: 'currentColor',
  },
  [IconName.CROSS]: {
    default: loadable(() => import('shared/svg/Cross')),
    defaultColor: 'primary40',
  },
  [IconName.CROSS_ICON]: {
    default: loadable(() => import('shared/svg/CrossIcon')),
    defaultColor: 'surface',
  },
  [IconName.CROSS_HAIR]: {
    default: loadable(() => import('shared/svg/CrossHair')),
    defaultColor: 'currentColor',
  },
  [IconName.DISCOUNT_TAG]: {
    default: loadable(() => import('shared/svg/DiscountTag')),
    defaultColor: 'currentColor',
  },
  [IconName.EASY_RETURNS]: {
    default: loadable(() => import('shared/svg/EasyReturns')),
    defaultColor: 'currentColor',
  },
  [IconName.GUEST_PROFILE]: {
    default: loadable(() => import('shared/svg/GuestProfile')),
    defaultColor: 'secondary',
  },
  [IconName.HELP_CENTER]: {
    default: loadable(() => import('shared/svg/HelpCenter')),
    defaultColor: 'textPrimary',
  },
  [IconName.LOCATION]: {
    default: loadable(() => import('shared/svg/Location')),
    defaultColor: 'currentColor',
  },
  [IconName.LOCK]: {
    default: loadable(() => import('shared/svg/Lock')),
    defaultColor: 'currentColor',
  },
  [IconName.NYKAA_LOGO]: {
    default: loadable(() => import('shared/svg/NykaaLogo')),
    defaultColor: 'textPrimary',
  },
  [IconName.PENCIL]: {
    default: loadable(() => import('shared/svg/Pencil')),
    defaultColor: 'primary40',
  },
  [IconName.PLUS_SVG]: {
    default: loadable(() => import('shared/svg/Plus')),
    defaultColor: 'textPrimary',
  },
  [IconName.PROFILE]: {
    default: loadable(() => import('shared/svg/Profile')),
    defaultColor: 'secondary',
  },
  [IconName.RATED_STAR]: {
    default: loadable(() => import('shared/svg/RatedStar')),
    defaultColor: 'currentColor',
  },
  [IconName.REFRESH]: {
    default: loadable(() => import('shared/svg/Refresh')),
    defaultColor: 'textSecondary',
  },
  [IconName.RELOAD]: {
    default: loadable(() => import('shared/svg/Reload')),
    defaultColor: 'primary40',
  },
  [IconName.SECURE_PAYMENT]: {
    default: loadable(() => import('shared/svg/SecurePayment')),
    defaultColor: 'currentColor',
  },
  [IconName.SHIPPING_N_RETURNS]: {
    default: loadable(() => import('shared/svg/ShippingNReturns/ShippingNReturns')),
    defaultColor: 'textPrimary',
  },
  [IconName.SQUARE_EXCLAMATION]: {
    default: loadable(() => import('shared/svg/SquareExclamation')),
    defaultColor: 'warning',
  },
  [IconName.TAG_RIBBON]: {
    default: loadable(() => import('shared/svg/TagRibbonSvg')),
    defaultColor: 'currentColor',
  },
  [IconName.THREE_DOTS]: {
    default: loadable(() => import('shared/svg/ThreeDots')),
    defaultColor: 'primary40',
  },
  [IconName.THUMBS_UP_SVG]: {
    default: loadable(() => import('shared/svg/ThumbsUp')),
    defaultColor: 'primary40',
  },
  [IconName.TICK]: {
    default: loadable(() => import('shared/svg/Tick')),
    defaultColor: 'positive',
  },
  [IconName.TICK_MARK]: {
    default: loadable(() => import('shared/svg/TickMark')),
    defaultColor: 'positive',
  },
  [IconName.TICK_ROUNDED]: {
    default: loadable(() => import('shared/svg/TickRounded')),
    defaultColor: 'positive',
  },
  [IconName.TRIANGLE_EXCLAMATION]: {
    default: loadable(() => import('shared/svg/TriangleExclamation')),
    defaultColor: 'warning',
  },
  [IconName.TRIANGLE_EXCLAMATION_EMPTY]: {
    default: loadable(() => import('shared/svg/TriangleExclamationEmpty')),
    defaultColor: 'warning',
  },
  [IconName.TRUCK_FIRE]: {
    default: loadable(() => import('shared/svg/TruckFire')),
    defaultColor: 'currentColor',
  },
  [IconName.UNRATED_STAR]: {
    default: loadable(() => import('shared/svg/UnratedStar')),
    defaultColor: 'currentColor',
  },
  [IconName.VERIFIED]: {
    default: loadable(() => import('shared/svg/Verified')),
    defaultColor: 'positive',
  },
};
